import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useWizard } from '../../contexts/WizardContext';
import { useWallet } from '../../contexts/WalletContext';
import { NETWORKS, NetworkConfig, getNetworkEngineType, isStellarNetwork } from '../../config/networks';
import { ethers } from 'ethers';
import { isStellarContractAddress, normalizeStellarAddress } from '../../services/stellar';
import { suggestDAppName } from '../../utils/nameSuggestions';
import './Step2BasicInfo.css';

const Step2BasicInfo: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const { walletKind } = useWallet();
  const [dAppName, setDAppName] = useState(wizardData.dAppName);
  const [selectedNetworkKey, setSelectedNetworkKey] = useState<string>('');
  const [contractAddress, setContractAddress] = useState(wizardData.contractAddress);
  const [contractError, setContractError] = useState('');
  const dAppNameTouchedRef = useRef(!!wizardData.dAppName.trim());
  const lastSuggestionRef = useRef('');

  const supportedNetworks = useMemo(() => {
    return (Object.entries(NETWORKS) as [string, NetworkConfig][]).filter(([, network]) => {
      if (!network.enabled) return false;
      if (!walletKind) return true;
      return getNetworkEngineType(network) === walletKind;
    });
  }, [walletKind]);

  const selectedNetwork = selectedNetworkKey ? NETWORKS[selectedNetworkKey] : wizardData.chain;
  const stellar = walletKind === 'stellar' || isStellarNetwork(selectedNetwork);

  // Auto-suggest dApp name from email / chain / contract (unless user edited it)
  useEffect(() => {
    const suggestion = suggestDAppName({
      email: wizardData.email,
      chain: selectedNetwork || wizardData.chain,
      contractAddress: contractAddress || wizardData.contractAddress,
    });
    if (!suggestion) return;

    setDAppName(prev => {
      const shouldApply =
        !dAppNameTouchedRef.current ||
        prev.trim() === '' ||
        prev === lastSuggestionRef.current;

      lastSuggestionRef.current = suggestion;

      if (shouldApply && prev !== suggestion) {
        updateWizardData({ dAppName: suggestion });
        dAppNameTouchedRef.current = false;
        return suggestion;
      }
      return prev;
    });
  }, [
    wizardData.email,
    wizardData.chain,
    wizardData.contractAddress,
    selectedNetwork,
    contractAddress,
    updateWizardData,
  ]);

  // Drop a previously selected chain if it no longer matches the connected wallet
  useEffect(() => {
    if (!wizardData.chain || !walletKind) return;
    if (getNetworkEngineType(wizardData.chain) === walletKind) return;

    setSelectedNetworkKey('');
    setContractAddress('');
    setContractError('');
    updateWizardData({
      chain: null,
      contractAddress: '',
      validationEngineProxyAddress: null,
      validationEngineImplementationAddress: null,
      isTrustlineFirewall: false,
      firewallTargetAddress: null,
      contractABI: '',
      methodMappings: [],
      onChainConfigSynced: false,
      sanctionsEnabled: null,
      sanctionsOracleKey: null,
    });
  }, [walletKind, wizardData.chain, updateWizardData]);

  useEffect(() => {
    if (wizardData.chain) {
      const match = supportedNetworks.find(
        ([, network]) =>
          network.chainId === wizardData.chain!.chainId &&
          getNetworkEngineType(network) === getNetworkEngineType(wizardData.chain)
      );
      if (match) {
        setSelectedNetworkKey(match[0]);
      } else {
        setSelectedNetworkKey('');
      }
    }
  }, [wizardData.chain, supportedNetworks]);

  const validateAddress = (address: string, network: NetworkConfig | null | undefined): string | null => {
    const trimmed = address.trim();
    if (!trimmed) return null;
    if (isStellarNetwork(network) || walletKind === 'stellar') {
      if (!isStellarContractAddress(trimmed)) {
        return 'Invalid Stellar contract address (expected C… StrKey)';
      }
      return null;
    }
    if (!ethers.isAddress(trimmed)) {
      return 'Invalid Ethereum address format';
    }
    return null;
  };

  const handleChainChange = (networkKey: string) => {
    setSelectedNetworkKey(networkKey);
    const network = NETWORKS[networkKey];
    if (!network) return;

    const nextAddress = contractAddress.trim();
    const error = validateAddress(nextAddress, network);
    setContractError(error || '');

    updateWizardData({
      chain: network,
      contractAddress: error || !nextAddress
        ? ''
        : isStellarNetwork(network)
          ? normalizeStellarAddress(nextAddress)
          : nextAddress,
      validationEngineProxyAddress: null,
      validationEngineImplementationAddress: null,
      isTrustlineFirewall: false,
      firewallTargetAddress: null,
      contractABI: '',
      methodMappings: [],
      onChainConfigSynced: false,
      sanctionsEnabled: null,
      sanctionsOracleKey: null,
    });

    if (!error && nextAddress) {
      setContractAddress(
        isStellarNetwork(network) ? normalizeStellarAddress(nextAddress) : nextAddress
      );
    } else if (error) {
      setContractAddress(nextAddress);
    }
  };

  const handleContractAddressChange = (address: string) => {
    setContractAddress(address);
    setContractError('');

    if (!address.trim()) {
      updateWizardData({ contractAddress: '' });
      return;
    }

    const network = selectedNetwork || wizardData.chain;
    const error = validateAddress(address, network);
    if (error) {
      setContractError(error);
      return;
    }

    const normalized = isStellarNetwork(network) || walletKind === 'stellar'
      ? normalizeStellarAddress(address)
      : address.trim();
    updateWizardData({ contractAddress: normalized });
  };

  const handleDAppNameChange = (name: string) => {
    dAppNameTouchedRef.current = true;
    setDAppName(name);
    updateWizardData({ dAppName: name });
  };

  const engineLabel = walletKind === 'stellar' ? 'Stellar' : walletKind === 'evm' ? 'EVM' : null;

  return (
    <div className="wizard-step step2-basic-info">
      <h2 className="wizard-step-title">Basic Information</h2>
      <p className="wizard-step-description">
        Provide the basic information about your dApp and smart contract. If your dApp is protected through a Trustline Firewall, enter the <strong>Firewall</strong> contract address here.
        {engineLabel && (
          <> Networks are limited to {engineLabel} based on your connected wallet.</>
        )}
      </p>

      <div className="wizard-form-group">
        <label htmlFor="chain-select">Chain *</label>
        <select
          id="chain-select"
          value={selectedNetworkKey}
          onChange={(e) => handleChainChange(e.target.value)}
          required
        >
          <option value="">Select a chain</option>
          {supportedNetworks.map(([key, network]) => (
            <option key={key} value={key}>
              {network.name} (Chain ID: {network.chainId})
            </option>
          ))}
        </select>
        {supportedNetworks.length === 0 && (
          <div className="wizard-error">
            No networks available for the connected wallet. Connect Freighter for Stellar or MetaMask for EVM.
          </div>
        )}
      </div>

      <div className="wizard-form-group">
        <label htmlFor="contract-address">Contract Address *</label>
        <input
          id="contract-address"
          type="text"
          value={contractAddress}
          onChange={(e) => handleContractAddressChange(e.target.value)}
          placeholder={stellar ? 'C…' : '0x...'}
          className={contractError ? 'error' : ''}
          required
        />
        {contractError && <div className="wizard-error">{contractError}</div>}
      </div>

      <div className="wizard-form-group">
        <label htmlFor="dapp-name">dApp Name *</label>
        <input
          id="dapp-name"
          type="text"
          value={dAppName}
          onChange={(e) => handleDAppNameChange(e.target.value)}
          placeholder="Suggested from your email, chain and contract"
          required
        />
        {!dAppNameTouchedRef.current && dAppName && (
          <small className="step2-hint">Suggested name — edit freely if you prefer another.</small>
        )}
      </div>
    </div>
  );
};

export default Step2BasicInfo;
